import React from "react";
import { useLocation } from "react-router-dom";
import { useGoogleAuth } from "@/contexts/GoogleAuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, AlertTriangle } from "lucide-react";

const LoginOverlay = () => {
  const { isAuthenticated, isLoading, login, supabaseSession, authError } = useGoogleAuth();
  const location = useLocation();
  
  // Don't show dialog if authenticated, still loading, or on public pages (landing page or request access page)
  if (isAuthenticated || isLoading || location.pathname === '/' || location.pathname === '/request-access') {
    return null;
  }

  return (
    <Dialog open={!isAuthenticated} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-xl">Welcome to ScoutLink.ai</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center space-y-4 py-6">
          <p className="text-center text-muted-foreground mb-4">
            Sign in with your Google Account to access your investor data and email drafts across devices
          </p>
          
          <Button
            onClick={login}
            className="flex items-center space-x-2 bg-white text-black border hover:bg-gray-100 w-64"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="24px" height="24px">
              <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
              <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
              <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
              <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
            </svg>
            <span>Sign in with Google</span>
          </Button>
          
          <p className="text-xs text-gray-500 mt-6 max-w-sm text-center">
            Signing in will allow us to sync your data between devices using Supabase,
            making it accessible across all your devices.
          </p>

          {authError && (
            <div className="mt-4 w-full border-t pt-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                <div>
                  <h3 className="text-xs font-medium text-amber-800">Authentication Issue</h3>
                  {authError === "email_not_confirmed" ? (
                    <p className="text-xs mt-1 text-amber-700">
                      Email verification required. For development, you may need to:
                    </p>
                  ) : (
                    <p className="text-xs mt-1 text-amber-700">
                      Login error: {authError}
                    </p>
                  )}
                  <ol className="list-decimal text-xs mt-1 ml-4 text-amber-700 space-y-1">
                    <li>Disable email confirmation in Supabase settings</li>
                    <li>Update the redirect URLs to include your current URL</li>
                  </ol>
                  <div className="flex flex-col gap-1 mt-2">
                    <Button 
                      variant="link" 
                      className="text-xs flex items-center justify-start px-0 text-amber-600 h-5"
                      onClick={() => window.open("https://supabase.com/dashboard/project/ahxwbmguupqmonzobezy/auth/providers", "_blank")}
                    >
                      Open Auth Settings <ExternalLink size={10} className="ml-1" />
                    </Button>
                    <Button 
                      variant="link" 
                      className="text-xs flex items-center justify-start px-0 text-amber-600 h-5"
                      onClick={() => window.open("https://supabase.com/dashboard/project/ahxwbmguupqmonzobezy/auth/url-configuration", "_blank")}
                    >
                      URL Configuration <ExternalLink size={10} className="ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginOverlay;
